import {
  Button,
  Form,
  FormInstance,
  Radio,
  Space,
  Typography,
  Upload,
} from "antd";
import { Plus, PlusCircle, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { extractImageExtension, getBase64 } from "../helpers";
import { Question } from "@types";

interface Props {
  form: FormInstance;
  question?: Question;
}

export const ImageChoiceQuestion = ({form, question}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string[]>([]);
  const [defaultCorrectAnswerIndex, setDefaultCorrectAnswerIndex] = useState<any>();

  const uploadButton = (
    <button
    style={{
      border: 0,
      background: "none",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    }}
    type="button"
  >
    {loading ? <LoadingOutlined /> : <Plus />}
    <Typography.Text style={{ marginTop: 8 }}>
      {t("forms.questions.selectImage")}
    </Typography.Text>
  </button>
  );

  useEffect(() => {
    if (question && question.answers) {
      const correctAnswerIndex = question.answers.findIndex((answer) => answer.isCorrect);
      setImageUrl(
        question.answers.map(
          (image) => `data:image/${image.answerImageViewModel?.extension};base64,${image.answerImageViewModel?.base64Value}`
        )
      );

      if (correctAnswerIndex >= 0) {
        setDefaultCorrectAnswerIndex(correctAnswerIndex);
      } else if (question.answers.length > 0) {
        // Ensure at least one correct answer is selected by default (first one)
        setDefaultCorrectAnswerIndex(0);
        question.answers.forEach((_, idx) => {
          form.setFieldValue(["answers", idx, "isCorrect"], idx === 0);
        });
      } else {
        setDefaultCorrectAnswerIndex(undefined);
      }
    }
  }, [question, form]);

  const handleChange = async (info: any, index: number, name: any) => {
    setLoading(true); // Set loading to true when image starts uploading
    let url = await getBase64(info.file.originFileObj);
    setLoading(false);
    const newImageUrl = [...imageUrl]; // Copy existing imageUrls
    newImageUrl[index] = url; // Replace imageUrl for the specific index
    form.setFieldValue(["answers", name, "answerImageViewModel", "extension"], extractImageExtension(url));
    form.setFieldValue(["answers", name, "answerImageViewModel", "base64Value"], url.split(",")[1]); 
    setImageUrl(newImageUrl); 
  };

  const handleChangeRadio = (e: any) => {
    const selectedIndex = e.target.value;
    setDefaultCorrectAnswerIndex(selectedIndex);
    const answers: any[] = form.getFieldValue(["answers"]) || [];
    answers.forEach((_, index) => {
      form.setFieldValue(["answers", index, "isCorrect"], index === selectedIndex);
    });
  };

  return (
    <>
      <Form.Item shouldUpdate noStyle>
        {({ getFieldValue }) => {
          const answers = getFieldValue(["answers"]) || [];
          const hasCorrect = answers.some((a: any) => a?.isCorrect);
          return !hasCorrect ? (
            <Space direction="vertical">
              <div style={{ marginBottom: 8 }}>
                <Typography.Text strong type="warning">
                  {t("forms.questions.oneAnswerCorrect")}
                </Typography.Text>
              </div>
            </Space>
          ) : null;
        }}
      </Form.Item>
      <Form.List name={["answers"]} initialValue={[{}]}>
        {(fields, { add, remove }) => (
          <Radio.Group style={{ width: "100%" }} value={defaultCorrectAnswerIndex} onChange={handleChangeRadio}>
            <Space
              wrap
              style={{ display: "flex", marginBottom: 8 }}
              align="baseline"
            >
              {fields.map(({ key, name, ...restField }, index) => (
                <Space key={key}>
                  <Form.Item
                    noStyle
                    initialValue={false}
                    {...restField}
                    getValueFromEvent={(e) => e.target.checked}
                    name={[name, "isCorrect"]}
                    valuePropName="checked"
                  >
                   <Radio value={index} />
                  </Form.Item>

                  <Form.Item
                    style={{ width: "100%" }}
                    {...restField}
                    name={[name, "answerImageViewModel"]}
                    rules={[
                      {
                        required: true,
                        message: t("forms.questions.selectImage"),
                      },
                    ]}
                  >
                    <Upload
                      name="avatar"
                      listType="picture-card"
                      className="avatar-uploader"
                      showUploadList={false}
                      // action="https://run.mocky.io/v3/45d0564a-e88d-42e2-a3c6-db64fb40b26d"
                      onChange={(info) => handleChange(info, index, name)} // Pass the index to handleChange
                    >
                      {imageUrl[index] ? (
                        <img
                          src={imageUrl[index]}
                          alt={t("forms.questions.selectImage")}
                          style={{ width: "100%" }}
                        />
                      ) : (
                        uploadButton
                      )}
                    </Upload>
                  </Form.Item>
                  <Button
                    icon={<Trash2 />}
                    danger
                    onClick={() => {
                      const wasSelected = index === defaultCorrectAnswerIndex;
                      const newImages = imageUrl.filter((_, filterIndex) => filterIndex !== index);
                      remove(name);
                      setImageUrl(newImages);

                      const answersAfterRemove: any[] = form.getFieldValue(["answers"]) || [];
                      if (answersAfterRemove.length === 0) {
                        setDefaultCorrectAnswerIndex(undefined);
                        return;
                      }

                      if (wasSelected) {
                        // select the first remaining as correct
                        setDefaultCorrectAnswerIndex(0);
                        answersAfterRemove.forEach((_, idx) => {
                          form.setFieldValue(["answers", idx, "isCorrect"], idx === 0);
                        });
                      } else if (index < (defaultCorrectAnswerIndex ?? 0)) {
                        // shift selection left if a prior item was removed
                        const newIndex = (defaultCorrectAnswerIndex ?? 0) - 1;
                        setDefaultCorrectAnswerIndex(newIndex);
                        answersAfterRemove.forEach((_, idx) => {
                          form.setFieldValue(["answers", idx, "isCorrect"], idx === newIndex);
                        });
                      } else {
                        // keep current selection index
                        answersAfterRemove.forEach((_, idx) => {
                          form.setFieldValue(["answers", idx, "isCorrect"], idx === (defaultCorrectAnswerIndex ?? 0));
                        });
                      }
                    }}
                  />
                </Space>
              ))}
            </Space>
            <Form.Item noStyle>
              <Button
                type="dashed"
                icon={<PlusCircle />}
                block
                onClick={() => {
                  const currentAnswers: any[] = form.getFieldValue(["answers"]) || [];
                  add();
                  // If none selected before, select the first after adding
                  const hadSelection = currentAnswers.some((a: any) => a?.isCorrect);
                  const updatedAnswers: any[] = form.getFieldValue(["answers"]) || [];
                  if (!hadSelection && updatedAnswers.length > 0) {
                    setDefaultCorrectAnswerIndex(0);
                    updatedAnswers.forEach((_, idx) => {
                      form.setFieldValue(["answers", idx, "isCorrect"], idx === 0);
                    });
                  }
                }}
              >
                {t("forms.questions.addAnswer")}
              </Button>
            </Form.Item>
          </Radio.Group>
        )}
      </Form.List>
      {/* Hidden validator to prevent save when no correct answer selected */}
      <Form.Item
        name={"__answersValidation"}
        dependencies={["answers"]}
        rules={[
          {
            validator: async () => {
              const answers: any[] = form.getFieldValue(["answers"]) || [];
              const hasCorrect = answers.some((a: any) => a?.isCorrect);
              if (!hasCorrect) {
                return Promise.reject(new Error(t("forms.questions.oneAnswerCorrect")));
              }
              return Promise.resolve();
            },
          },
        ]}
        style={{ display: "none" }}
      >
        <input />
      </Form.Item>
    </>
  );
};
